/**
 * Confirming what was read, before it counts.
 *
 * Nothing the scanner produces is used until it appears here and the user
 * accepts it. Values read cleanly show as numbers; values the pipeline could
 * not vouch for show as empty fields asking to be filled, rather than as a
 * number with a small warning next to it — because a number with a warning
 * next to it gets read as a number.
 *
 * Anything typed here becomes `source: 'user'`, which overrides OCR outright
 * and is exempt from the confidence floor. The person holding the packet is a
 * better authority than the camera.
 */

import { useMemo, useState } from 'react';
import type {
  NutrientKey,
  NutrientResult,
  NutritionLabel,
  ScanResult,
  ValidationIssue,
} from '@/lib/scanner/types';
import { isTrustworthy } from '@/lib/scanner/validate';
import type { ScanEntry } from '@/lib/scanner/calculate';

/** Shown in this order; the first four are what the comparison needs. */
const FIELDS: Array<{ key: NutrientKey; label: string; unit: string; required: boolean }> = [
  { key: 'calories', label: 'Energy', unit: 'kcal', required: true },
  { key: 'protein', label: 'Protein', unit: 'g', required: true },
  { key: 'totalCarbohydrate', label: 'Carbohydrate', unit: 'g', required: true },
  { key: 'totalFat', label: 'Fat', unit: 'g', required: true },
  { key: 'saturatedFat', label: 'Saturated fat', unit: 'g', required: false },
  { key: 'totalSugars', label: 'Sugars', unit: 'g', required: false },
  { key: 'dietaryFiber', label: 'Fibre', unit: 'g', required: false },
  { key: 'sodium', label: 'Sodium', unit: 'mg', required: false },
];

export function ScanReview({
  result,
  entry,
  currency,
  onEntryChange,
  onConfirm,
  onRetake,
  onCancel,
}: {
  result: ScanResult;
  entry: ScanEntry;
  currency: string;
  onEntryChange: (entry: ScanEntry) => void;
  onConfirm: (label: NutritionLabel) => void;
  onRetake: () => void;
  onCancel: () => void;
}): React.JSX.Element {
  // Edits live here until confirmed, so cancelling really does discard them.
  const [edits, setEdits] = useState<Partial<Record<NutrientKey, string>>>({});
  const [basis, setBasis] = useState(result.label.basis);

  const valueFor = (key: NutrientKey): string => {
    const edited = edits[key];
    if (edited !== undefined) return edited;
    const n = result.label.nutrients[key];
    if (n === undefined || n.value === null) return '';
    // A value the pipeline does not vouch for is presented as MISSING, not as
    // a number wearing a warning.
    return isTrustworthy(n.confidence) ? String(n.value) : '';
  };

  const statusFor = (key: NutrientKey): 'read' | 'unsure' | 'missing' | 'edited' => {
    if (edits[key] !== undefined) return 'edited';
    const n = result.label.nutrients[key];
    if (n === undefined || n.value === null) return 'missing';
    return isTrustworthy(n.confidence) ? 'read' : 'unsure';
  };

  const issuesFor = (key: NutrientKey): ValidationIssue[] =>
    result.issues.filter((i) => i.field === key);

  const merged = useMemo((): NutritionLabel => {
    const nutrients: Partial<Record<NutrientKey, NutrientResult>> = {};
    for (const field of FIELDS) {
      const raw = valueFor(field.key);
      const existing = result.label.nutrients[field.key];
      if (raw.trim() === '') continue;
      const value = Number(raw);
      if (!Number.isFinite(value) || value < 0) continue;

      const edited = edits[field.key] !== undefined;
      nutrients[field.key] = {
        name: field.key,
        value,
        unit: existing?.unit ?? field.unit,
        // A typed value is certain by definition: the user is looking at the
        // packet. It also bypasses the confidence floor downstream.
        confidence: edited ? 1 : (existing?.confidence ?? 0.5),
        source: edited ? 'user' : (existing?.source ?? 'ocr'),
        dailyValue: existing?.dailyValue ?? null,
        warnings: edited ? [] : (existing?.warnings ?? []),
        agreement: existing?.agreement,
      };
    }
    return { ...result.label, basis, nutrients };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edits, basis, result]);

  const readyCount = FIELDS.filter((f) => f.required && valueFor(f.key) !== '').length;
  const canConfirm = readyCount >= 2;
  const needsServing = basis === 'per_serving' && (result.label.servingSize?.grams ?? null) === null;

  return (
    <div className="scan-review">
      <div className="scan-review-head">
        <h3>Check what was read</h3>
        <span className="scan-elapsed">{(result.elapsedMs / 1000).toFixed(1)}s</span>
      </div>

      {/* The basis governs every later number, so it is confirmed explicitly
          rather than assumed from a detection that can be wrong. */}
      <label className="scan-field scan-basis">
        <span className="scan-field-label">These numbers are per</span>
        <select value={basis} onChange={(e) => setBasis(e.target.value as typeof basis)}>
          <option value="per_100g">100 g</option>
          <option value="per_100ml">100 ml</option>
          <option value="per_serving">
            serving{result.label.servingSize ? ` (${result.label.servingSize.text})` : ''}
          </option>
        </select>
      </label>

      {needsServing && (
        <p className="scan-issue is-error">
          A per-serving panel needs the serving size in grams before it can be compared. Switch to
          per 100 g, or retake so the serving line is in frame.
        </p>
      )}

      {result.label.warnings.map((w) => (
        <p className="scan-issue" key={w}>
          {w}
        </p>
      ))}
      {result.issues
        .filter((i) => i.field === 'label')
        .map((i) => (
          <p className={`scan-issue is-${i.severity}`} key={i.message}>
            {i.message} {i.reason}
          </p>
        ))}

      <div className="scan-fields">
        {FIELDS.map((field) => {
          const status = statusFor(field.key);
          const issues = issuesFor(field.key);
          return (
            <div className={`scan-field is-${status}`} key={field.key}>
              <label className="scan-field-label" htmlFor={`scan-${field.key}`}>
                {field.label}
                {field.required && <span aria-hidden="true"> *</span>}
              </label>
              <div className="scan-field-input">
                <input
                  id={`scan-${field.key}`}
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step="any"
                  placeholder={status === 'unsure' ? 'Could not read — type it' : '—'}
                  value={valueFor(field.key)}
                  onChange={(e) =>
                    setEdits((cur) => ({ ...cur, [field.key]: e.target.value }))
                  }
                />
                <span className="scan-field-unit">{field.unit}</span>
                <StatusMark status={status} />
              </div>
              {issues.map((i) => (
                <p className={`scan-field-note is-${i.severity}`} key={i.message}>
                  {i.reason}
                </p>
              ))}
              {status === 'unsure' && issues.length === 0 && (
                <p className="scan-field-note">
                  We could not read this reliably, so it is left blank rather than guessed.
                </p>
              )}
            </div>
          );
        })}
      </div>

      <div className="scan-entry">
        <h4>What you paid</h4>
        <div className="scan-entry-row">
          <label className="scan-field">
            <span className="scan-field-label">Price ({currency})</span>
            <input
              type="number"
              inputMode="decimal"
              min={0}
              step="any"
              value={entry.price ?? ''}
              onChange={(e) =>
                onEntryChange({
                  ...entry,
                  price: e.target.value === '' ? null : Number(e.target.value),
                })
              }
            />
          </label>

          <label className="scan-field">
            <span className="scan-field-label">Pack size</span>
            <input
              type="number"
              inputMode="decimal"
              min={0}
              step="any"
              value={entry.packAmount ?? ''}
              onChange={(e) =>
                onEntryChange({
                  ...entry,
                  packAmount: e.target.value === '' ? null : Number(e.target.value),
                })
              }
            />
          </label>

          <label className="scan-field scan-field-narrow">
            <span className="scan-field-label">Unit</span>
            <select
              value={entry.packUnit}
              onChange={(e) =>
                onEntryChange({ ...entry, packUnit: e.target.value as 'g' | 'ml' })
              }
            >
              <option value="g">g</option>
              <option value="ml">ml</option>
            </select>
          </label>
        </div>

        <label className="scan-field">
          <span className="scan-field-label">Name it (optional)</span>
          <input
            type="text"
            maxLength={40}
            placeholder="so you can tell the two apart"
            value={entry.nickname ?? ''}
            onChange={(e) => onEntryChange({ ...entry, nickname: e.target.value })}
          />
        </label>

        <p className="scan-field-note">
          Price and pack size are needed for the per-dollar figures. Without them you still get the
          per-100 comparison.
        </p>
      </div>

      <div className="scan-actions">
        <button
          type="button"
          className="btn btn-primary"
          disabled={!canConfirm}
          onClick={() => onConfirm(merged)}
        >
          Use this
        </button>
        <button type="button" className="btn" onClick={onRetake}>
          Retake
        </button>
        <button type="button" className="btn" onClick={onCancel}>
          Discard
        </button>
      </div>

      {!canConfirm && (
        <p className="scan-field-note">
          At least two of energy, protein, carbohydrate and fat are needed before this can be
          compared.
        </p>
      )}
    </div>
  );
}

function StatusMark({ status }: { status: 'read' | 'unsure' | 'missing' | 'edited' }): React.JSX.Element {
  const map = {
    read: { mark: '✓', title: 'Read from the label' },
    edited: { mark: '✎', title: 'You typed this' },
    unsure: { mark: '⚠', title: 'Could not be read reliably' },
    missing: { mark: '—', title: 'Not found on the label' },
  } as const;
  const { mark, title } = map[status];
  return (
    <span className={`scan-mark is-${status}`} title={title}>
      <span aria-hidden="true">{mark}</span>
      <span className="visually-hidden">{title}</span>
    </span>
  );
}
